// §4 S4 — Dispatch board, /production/dispatch (Module 04, slice 04-A).
//
// THE D-072 RULE — SERVED ORDER IS LAW. Each column is one station queue (§5
// row 14), served in `priority ASC, scheduled_start_at ASC NULLS LAST,
// due_date ASC NULLS LAST, wo_no ASC, seq_no ASC` — computed by the SERVER's
// comparator, the only one that exists. This screen renders `operations[]` in
// ARRAY ORDER and NEVER re-sorts: no client comparator, no "stable sort just
// in case". A unit test pins that a deliberately scrambled payload renders
// verbatim.
//
// DRAG IS A PRIORITY SETTER, NOT A KANBAN (§4 S4, amended in this change):
// a within-column drop maps the position to a WO-level priority 1–5 and writes
// `PATCH /v1/work-orders/{id} {priority}`. On success every station queue is
// invalidated (priority is WO-level — every column carrying one of its ops
// re-orders) and the board re-renders the RE-FETCHED served order; the card
// may legally not land where it was dropped, and the toast says the order is
// computed. Cross-column drags are scheduling and are refused naming Module
// 03's Gantt. Drag is disabled below `md` (the phone reads, it does not
// re-plan) and gates on `production.wo.write` — the §4 audience (planner /
// facility_admin / group_admin) is exactly that holder set.
//
// One dead station must not blank the board: states are PER COLUMN, and a 404
// station renders as an absent column with a note (§9.14 — a work centre that
// no longer exists answers 404, same as one that never did).
//
// AT A FACILITY WITH A PUBLISHED SCHEDULE (P3-I-2, D-399 ruling 7) the drag
// still writes priority, the PATCH emits `production.work_order_reprioritized`
// and module 03 solves at once. Every card of the dragged work order reads
// "Schedule updating" until the facility's published schedule changes, which
// the board watches every 5 seconds while a card waits, and then every column
// re-reads its served order. The marker also clears when the solve the drag
// caused ends without publishing (a draft waiting for a person, the short
// circuit, a failure), which the board reads from the facility's solve runs,
// and in any case after 10 minutes, so a facility that publishes by hand never
// polls for ever (D-411 ruling 11). A reader without `planning.schedule.read`
// sees no marker, because the board cannot tell when the schedule moved.

import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { PageHeader } from "../../../../components/breadcrumbs";
import { Button } from "../../../../components/button";
import { Field } from "../../../../components/field";
import { CardSkeleton, ErrorState } from "../../../../components/states";
import { StatusChip } from "../../../../components/status-chip";
import { UnauthorizedError } from "../../../../lib/problem";
import { RequestError, requestIdOf } from "../../../../lib/request";
import { useTerm } from "../../../onboarding/hooks/terminology";
import { usePlanningPermissions } from "../../planning/hooks/permissions";
import { useScheduleHeads, useScheduleRuns } from "../../planning/hooks/schedule-queries";
import { FacilityFilter } from "../components/facility-filter";
import {
  useDepartments,
  useFacilities,
  usePatchWorkOrder,
  useStationQueue,
  useWorkCenters,
  type StationQueueOperation,
} from "../hooks/production-queries";
import { useProductionPermissions } from "../hooks/permissions";
import { useIsDesktop } from "../hooks/use-is-desktop";
import {
  formatBusinessDate,
  formatQty,
  localeLabel,
  productionChipVariant,
  productionErrorMessage,
} from "../lib/format";
import { validateDispatchSearch } from "./search-params";
import { pickableWorkCenters } from "../../../../lib/work-centers";

/** How long a "Schedule updating" mark may wait: the solve's lease (D-398 ruling 5). */
const SCHEDULE_UPDATING_MAX_MS = 10 * 60_000;

interface UpdatingMark {
  /** The published schedule the drag was made against. */
  readonly publishedId: string;
  /** The server's time of the drag's write. */
  readonly since: string;
  /** This browser's time of the mark, for the bound. */
  readonly markedAt: number;
}

interface DragSource {
  readonly workOrderId: string;
  readonly opId: string;
  readonly workCenterId: string;
}

export function DispatchBoardScreen() {
  const { t, i18n } = useTranslation();
  const { term } = useTerm();
  const navigate = useNavigate();
  const perms = useProductionPermissions();
  const isDesktop = useIsDesktop();

  const rawSearch = useSearch({ strict: false });
  const search = useMemo(
    () => validateDispatchSearch(rawSearch as Record<string, unknown>),
    [rawSearch],
  );
  const facilities = useFacilities();
  // The board opens on the reader's facility (D-449): the first one their
  // scope reaches, until they pick another. "Any facility" showed nothing,
  // because a board is one plant's stations.
  const facilityId = search.facility_id ?? facilities.data?.items[0]?.id ?? "";
  const departmentId = search.department_id ?? "";
  const departments = useDepartments(facilityId);
  const workCenters = useWorkCenters(facilityId.length > 0 ? facilityId : undefined);

  const patch = usePatchWorkOrder();
  const [drag, setDrag] = useState<DragSource | undefined>(undefined);
  const [notice, setNotice] = useState<string | undefined>(undefined);

  // Work order id to the published schedule it was dragged against and the
  // server's time of the drag's write.
  const planningPerms = usePlanningPermissions();
  const readsSchedule = planningPerms.has("planning.schedule.read");
  const [updating, setUpdating] = useState<ReadonlyMap<string, UpdatingMark>>(new Map());
  const waiting = updating.size > 0;
  const heads = useScheduleHeads(facilityId, readsSchedule, waiting);
  const runs = useScheduleRuns(facilityId, undefined, readsSchedule && waiting);
  const publishedId = heads.data?.published?.id ?? null;

  useEffect(() => {
    setUpdating(new Map());
  }, [facilityId]);

  // A board with no facility to open on cannot ask for one when the list
  // itself failed: that is an error with a retry, or a sign-in.
  const facilitiesError = facilities.error;
  useEffect(() => {
    if (facilitiesError instanceof UnauthorizedError) void navigate({ to: "/login" });
  }, [facilitiesError, navigate]);

  // A new published schedule ends every mark made against an older one.
  useEffect(() => {
    if (publishedId === null) return;
    setUpdating((prev) => {
      if (![...prev.values()].some((m) => m.publishedId !== publishedId)) return prev;
      return new Map([...prev].filter(([, m]) => m.publishedId === publishedId));
    });
  }, [publishedId]);

  // A solve that started after the drag ended without publishing, and none
  // runs or waits: the schedule will not move for this drag (D-411 ruling 11).
  const runList = runs.data;
  useEffect(() => {
    if (runList === undefined) return;
    if (runList.pending_request !== null) return;
    if (runList.items.some((r) => r.status === "running")) return;
    setUpdating((prev) => {
      const settled = [...prev].filter(([, m]) =>
        runList.items.some((r) => Date.parse(r.started_at) >= Date.parse(m.since)),
      );
      if (settled.length === 0) return prev;
      const next = new Map(prev);
      for (const [id] of settled) next.delete(id);
      return next;
    });
  }, [runList]);

  // The bound: a mark never outlives the solve's 10-minute lease.
  useEffect(() => {
    if (!waiting) return;
    const timer = setTimeout(() => {
      const cutoff = Date.now() - SCHEDULE_UPDATING_MAX_MS;
      setUpdating((prev) => new Map([...prev].filter(([, m]) => m.markedAt > cutoff)));
    }, SCHEDULE_UPDATING_MAX_MS);
    return () => {
      clearTimeout(timer);
    };
  }, [waiting, updating]);

  const canDrag = perms.has("production.wo.write") && isDesktop;

  const columns = useMemo(
    () =>
      pickableWorkCenters(workCenters.data?.items).filter(
        (wc) => wc.department_id === departmentId,
      ),
    [workCenters.data, departmentId],
  );

  /**
   * A drop at position `index` in a column asks for priority `index + 1`,
   * clamped to the §2.1 CHECK range 1–5 — the request, not the outcome: the
   * server recomputes the whole order and the re-fetch is what renders.
   */
  const handleDrop = (targetWorkCenterId: string, index: number) => {
    if (drag === undefined) return;
    const source = drag;
    setDrag(undefined);
    if (source.workCenterId !== targetWorkCenterId) {
      // Cross-work-centre moves are SCHEDULING — Module 03's Gantt (D-071).
      setNotice(t("production.dispatch.crossColumnRefused"));
      return;
    }
    const priority = Math.min(Math.max(index + 1, 1), 5);
    patch.mutate(
      { id: source.workOrderId, body: { priority } },
      {
        onSuccess: (data) => {
          setNotice(t("production.dispatch.priorityToast", { value: priority }));
          if (publishedId !== null) {
            const mark: UpdatingMark = {
              publishedId,
              // The server's clock, which the runs' start times are on.
              since: data.work_order.updated_at,
              markedAt: Date.now(),
            };
            setUpdating((prev) => new Map(prev).set(source.workOrderId, mark));
          }
        },
        onError: (error) => {
          setNotice(productionErrorMessage(t, error));
        },
      },
    );
  };

  return (
    <section className="dispatch-board">
      <PageHeader title={t("production.dispatch.title")} />

      <div className="board-controls">
        <FacilityFilter
          value={facilityId}
          facilities={facilities.data?.items ?? []}
          onChange={(value) => {
            void navigate({
              to: "/production/dispatch",
              search: value.length > 0 ? { facility_id: value } : {},
              replace: true,
            });
          }}
        />
        {facilityId.length > 0 ? (
          <Field label={term("department")}>
            <select
              value={departmentId}
              onChange={(event) => {
                const value = event.target.value;
                void navigate({
                  to: "/production/dispatch",
                  search:
                    value.length > 0
                      ? { facility_id: facilityId, department_id: value }
                      : { facility_id: facilityId },
                  replace: true,
                });
              }}
            >
              <option value="">{t("production.list.filter.any")}</option>
              {(departments.data?.items ?? []).map((department) => (
                <option key={department.id} value={department.id}>
                  {department.name}
                </option>
              ))}
            </select>
          </Field>
        ) : null}
      </div>

      {canDrag ? <p className="production-hint">{t("production.dispatch.dragHint")}</p> : null}
      <p className="production-mobile-hint">{t("production.dispatch.readOnlyPhone")}</p>

      {notice !== undefined ? (
        <p className="production-note" role="status" data-testid="dispatch-notice">
          {notice}
        </p>
      ) : null}

      {facilityId.length === 0 && facilitiesError !== null ? (
        facilitiesError instanceof UnauthorizedError ? null : (
          <ErrorState
            title={t("production.dispatch.facilitiesError")}
            retryLabel={t("production.common.retry")}
            onRetry={() => void facilities.refetch()}
            requestId={requestIdOf(facilitiesError)}
            requestIdLabel={t("production.common.requestId")}
          />
        )
      ) : facilityId.length === 0 && facilities.isPending ? (
        <div data-testid="dispatch-facilities-loading">
          <CardSkeleton lines={4} label={t("production.common.loading")} />
        </div>
      ) : facilityId.length === 0 ? (
        <p className="production-note">{t("production.dispatch.pickFacilityFirst")}</p>
      ) : departmentId.length === 0 ? (
        <p className="production-note">{t("production.dispatch.pickDepartmentFirst")}</p>
      ) : workCenters.isPending ? (
        <CardSkeleton lines={4} label={t("production.common.loading")} />
      ) : columns.length === 0 ? (
        <p className="production-note">{t("production.dispatch.noWorkCenters")}</p>
      ) : (
        <div
          className="board"
          role="list"
          aria-label={t("production.dispatch.boardAria")}
          data-testid="dispatch-board"
        >
          {columns.map((wc) => (
            <StationColumn
              key={wc.id}
              workCenterId={wc.id}
              title={localeLabel(wc.name_i18n, i18n.language)}
              canDrag={canDrag}
              dragging={drag}
              onDragStart={setDrag}
              onDrop={handleDrop}
              updating={updating}
              publishedId={publishedId}
            />
          ))}
        </div>
      )}
    </section>
  );
}

function StationColumn({
  workCenterId,
  title,
  canDrag,
  dragging,
  onDragStart,
  onDrop,
  updating,
  publishedId,
}: {
  workCenterId: string;
  title: string;
  canDrag: boolean;
  dragging: DragSource | undefined;
  onDragStart: (source: DragSource | undefined) => void;
  onDrop: (workCenterId: string, index: number) => void;
  /** Work orders whose cards read "Schedule updating" until the next publish. */
  updating: ReadonlyMap<string, UpdatingMark>;
  /** The facility's published schedule; a new one re-reads the served order. */
  publishedId: string | null;
}) {
  const { t, i18n } = useTranslation();
  const queue = useStationQueue(workCenterId);
  const refetch = queue.refetch;
  const [seenPublished, setSeenPublished] = useState(publishedId);
  useEffect(() => {
    if (publishedId === seenPublished) return;
    setSeenPublished(publishedId);
    if (seenPublished !== null) void refetch();
  }, [publishedId, seenPublished, refetch]);

  if (queue.error instanceof RequestError && queue.error.httpStatus === 404) {
    // §9.14: a station that no longer resolves is an ABSENT column with a
    // note, not a dead board.
    return (
      <div className="board-column" role="listitem" data-testid={`column-${workCenterId}`}>
        <h3>{title}</h3>
        <p className="production-note">{t("production.dispatch.stationAbsent")}</p>
      </div>
    );
  }

  // THE SERVED ARRAY IS THE ORDER (D-072): rendered verbatim, never re-sorted.
  const operations: readonly StationQueueOperation[] = queue.data?.operations ?? [];

  return (
    <div
      className="board-column"
      role="listitem"
      data-testid={`column-${workCenterId}`}
      onDragOver={(event) => {
        if (dragging !== undefined) event.preventDefault();
      }}
      onDrop={(event) => {
        event.preventDefault();
        // Dropping on column background = after the last card.
        onDrop(workCenterId, operations.length > 0 ? operations.length - 1 : 0);
      }}
    >
      <h3>{title}</h3>
      {queue.isPending ? (
        <div className="state-skeleton" role="status" aria-label={t("production.common.loading")}>
          <div className="skeleton-row">
            <span className="skeleton" />
          </div>
          <div className="skeleton-row">
            <span className="skeleton" />
          </div>
        </div>
      ) : queue.error ? (
        <div className="state state-error" role="alert">
          <p className="state-title">{t("production.dispatch.columnError")}</p>
          <Button onClick={() => void queue.refetch()}>{t("production.common.retry")}</Button>
        </div>
      ) : operations.length === 0 ? (
        <p className="production-hint">{t("production.dispatch.emptyColumn")}</p>
      ) : (
        operations.map((op, index) => (
          <div
            key={op.id}
            className={dragging?.opId === op.id ? "board-card is-dragging" : "board-card"}
            data-testid={`queue-card-${op.id}`}
            draggable={canDrag}
            onDragStart={(event) => {
              if (!canDrag) return;
              onDragStart({ workOrderId: op.work_order_id, opId: op.id, workCenterId });
              try {
                event.dataTransfer.setData("text/plain", op.id);
              } catch {
                // jsdom and some browsers restrict dataTransfer — the React
                // state above is the source of truth.
              }
            }}
            onDragEnd={() => {
              onDragStart(undefined);
            }}
            onDragOver={(event) => {
              if (dragging !== undefined) {
                event.preventDefault();
                event.stopPropagation();
              }
            }}
            onDrop={(event) => {
              event.preventDefault();
              event.stopPropagation();
              onDrop(workCenterId, index);
            }}
          >
            <div className="board-card-title">
              {op.wo_no} · {op.product_name}
            </div>
            <div className="board-card-meta">
              <StatusChip variant={productionChipVariant(op.status)} className="status-chip">
                {t(`production.status.op.${op.status}`)}
              </StatusChip>
              {/* THE BLOCKED MARKERS, which this card has had the data for
                  since 04-C-5 and never drew. `qc_hold` and `rework_reopened_at`
                  have travelled the whole path — column, repo select, row type,
                  serializer, contract, generated client, into `operations[]` —
                  and were dropped right here, while the S2 Operations tab and
                  the floor station card both render them. The cost is specific
                  and this screen's own: a planner drags a card to the top of a
                  column to make it next, and a held step cannot be started by
                  anyone, so the re-prioritisation moves work that will not move.
                  A status chip cannot express either fact — a held step reads
                  `in_progress` exactly like one running normally, and a
                  re-opened step reads `in_progress` exactly like a first
                  attempt. */}
              {op.rework_reopened_at !== null ? (
                <span className="production-badge" data-testid={`queue-card-rework-${op.id}`}>
                  {t("production.dispatch.cardReworkBadge")}
                </span>
              ) : null}
              {updating.has(op.work_order_id) ? (
                <span
                  className="production-badge"
                  role="status"
                  data-testid={`queue-card-schedule-updating-${op.id}`}
                >
                  {t("production.dispatch.scheduleUpdating")}
                </span>
              ) : null}
              {op.qc_hold ? (
                <span
                  className="production-badge is-warn"
                  data-testid={`queue-card-hold-${op.id}`}
                  title={t("production.dispatch.cardBlockedAria")}
                >
                  {t("production.dispatch.cardHoldBadge")}
                </span>
              ) : null}
              <span>
                {t("production.dispatch.qtyOf", {
                  reported: formatQty(op.qty_reported, { locale: i18n.language }),
                  planned: formatQty(op.qty_planned, { locale: i18n.language }),
                })}
              </span>
              <span>{t("production.common.priorityLabel", { value: op.priority })}</span>
              {op.due_date !== null ? (
                <span>
                  {t("production.dispatch.dueOn", {
                    date: formatBusinessDate(op.due_date, i18n.language),
                  })}
                </span>
              ) : null}
            </div>
          </div>
        ))
      )}
    </div>
  );
}
